import { Box, Flex, Heading, Text } from '@chakra-ui/layout';
import { Divider, Image, Input, Textarea } from '@chakra-ui/react';

import { globeImage } from '@revelio/assets';

import { useBrand } from '../../../../shared/branding/brand-provider';
import {
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_WIDTH,
  REPORT_THEME,
} from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { useCoverSelectedParameters } from './useCoverSelectedParameters';

type ReportCoverPageProps = {
  title: string;
  description: string;
  isEditMode?: boolean;
};

export const ReportCoverPage = ({
  title,
  description,
  isEditMode = false,
}: ReportCoverPageProps) => {
  const brand = useBrand();
  const titleColor = brand.coverTitleColor;
  const descriptionColor = brand.coverDescriptionColor;
  const { dispatch } = useTabContentConfig();

  const handleTitleChange = (newTitle: string) => {
    dispatch({ type: 'UPDATE_REPORT_TITLE', payload: { title: newTitle } });
  };

  const handleDescriptionChange = (newDescription: string) => {
    dispatch({
      type: 'UPDATE_REPORT_DESCRIPTION',
      payload: { description: newDescription },
    });
  };
  const topPercent = (8 / DEFAULT_GRID_ROWS) * 100;

  const halfWidth = REPORT_PAGE_WIDTH / 2;
  const reportCenterPadding = `max(calc(50% - ${halfWidth}px), ${REPORT_THEME.margins.minCenterMargin}px)`;

  const hasCoverImage = !!brand.coverImagePortrait;

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      data-cover-page="true"
      backgroundImage={
        hasCoverImage
          ? undefined
          : `linear-gradient(to bottom, ${brand.sidebarColor[900]}, ${brand.sidebarColor[500]})`
      }
      backgroundColor={hasCoverImage ? brand.coverBackgroundColor : undefined}
      overflow="hidden"
      sx={{
        WebkitPrintColorAdjust: 'exact !important',
        printColorAdjust: 'exact !important',
      }}
    >
      {hasCoverImage ? (
        <Image
          src={brand.coverImagePortrait}
          alt={`${brand.displayName} cover`}
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          objectFit="cover"
          objectPosition="bottom left"
          loading="eager"
        />
      ) : (
        <Image
          src={globeImage}
          alt="Report Cover"
          position="absolute"
          right={0}
          bottom={0}
          width="80%"
          maxWidth="1400px"
          height="auto"
          maxHeight="600px"
          loading="eager"
        />
      )}
      <Image
        src={brand.logos.reportLogo}
        alt={`${brand.displayName} logo`}
        position="absolute"
        left={reportCenterPadding}
        top={REPORT_THEME.margins.pdf}
        height="20px"
        width="auto"
        zIndex={2}
        loading="eager"
      />
      <Box
        position="absolute"
        left={reportCenterPadding}
        right={reportCenterPadding}
        top={`${topPercent}%`}
        zIndex={1}
        color={titleColor}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      >
        {title !== undefined &&
          (isEditMode ? (
            <Input
              value={title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              variant="unstyled"
              fontSize="48px"
              fontWeight="bold"
              color={titleColor}
              padding={0}
              height="auto"
              lineHeight="1.2"
              sx={{
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '&:focus': {
                  borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: 'none',
                },
                '&:hover': {
                  borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                },
              }}
              placeholder="Title"
              marginBottom="1rem"
            />
          ) : (
            <Heading as="h1" size="2xl" fontWeight="bold" marginBottom="1rem">
              {title || 'Title'}
            </Heading>
          ))}
        {description !== undefined &&
          (isEditMode ? (
            <Textarea
              value={description || ''}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              variant="unstyled"
              fontSize="md"
              color={descriptionColor}
              padding={0}
              lineHeight="1.5"
              sx={{
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '&:focus': {
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: 'none',
                },
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                },
              }}
              placeholder="Description"
              marginTop={title !== undefined ? '0.5rem' : '0'}
              rows={6}
              resize="vertical"
            />
          ) : (
            <Text
              fontSize="md"
              marginTop={title !== undefined ? '0.5rem' : '0'}
              color={descriptionColor}
            >
              {description || 'Description'}
            </Text>
          ))}
        <CoverPageParameterSelections
          titleColor={titleColor}
          descriptionColor={descriptionColor}
        />
      </Box>
    </Box>
  );
};

const CoverPageParameterSelections = ({
  titleColor,
  descriptionColor,
}: {
  titleColor: string;
  descriptionColor: string;
}) => {
  const parameters = useCoverSelectedParameters();

  const mid = Math.ceil(parameters.length / 2);
  const leftColumn = parameters.slice(0, mid);
  const rightColumn = parameters.slice(mid);

  return (
    <Flex
      flexDirection="row"
      alignItems="flex-start"
      gap="1.5rem"
      height="44px"
      marginTop="1.5rem"
    >
      <Box minWidth={0}>
        {leftColumn.map((param) => (
          <Box
            key={param.label}
            marginBottom={rightColumn.length > 0 ? '0.75rem' : 0}
          >
            <Text fontSize="sm" fontWeight="bold" color={titleColor}>
              {param.label}
            </Text>
            <Text fontSize="sm" color={descriptionColor}>
              {param.value}
            </Text>
          </Box>
        ))}
      </Box>
      {rightColumn.length > 0 && (
        <>
          <Divider orientation="vertical" color={titleColor} marginX="1.5rem" />
          <Box minWidth={0}>
            {rightColumn.map((param) => (
              <Box key={param.label} marginBottom="0.75rem">
                <Text fontSize="sm" fontWeight="bold" color={titleColor}>
                  {param.label}
                </Text>
                <Text fontSize="sm" color={descriptionColor}>
                  {param.value}
                </Text>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Flex>
  );
};
